import { detect } from 'detect-browser';
import { getAppInfo, getAppPlatform } from 'students/utils';
import type { IApiErrorEntry, IConsoleEntry, IFeedbackContext } from './types';

const browser = detect();

const CONSOLE_NOISE_PATTERNS = [
  /^Warning:/,
  /^\[HMR\]/,
  /^%c/,
  /^Download the React DevTools/,
  /chrome-extension:\/\//,
  /safari-extension:\/\//
];

const MAX_ENTRIES = 10;

class FeedbackContextCollector {
  private consoleErrors: IConsoleEntry[] = [];

  private apiErrors: IApiErrorEntry[] = [];

  private initialized = false;

  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.patchConsoleError();
  }

  private patchConsoleError(): void {
    const original = console.error;
    const self = this;
    console.error = function (...args: unknown[]) {
      const message = args.map(String).join(' ');
      if (!CONSOLE_NOISE_PATTERNS.some((p) => p.test(message))) {
        self.pushEntry(self.consoleErrors, {
          message: message.slice(0, 500),
          timestamp: new Date().toISOString()
        });
      }
      original.apply(console, args);
    };
  }

  pushApiError(entry: IApiErrorEntry): void {
    this.pushEntry(this.apiErrors, entry);
  }

  async getSnapshot(locale: string): Promise<IFeedbackContext> {
    const platform = getAppPlatform();

    let appVersion = 'unknown';
    try {
      const info = await getAppInfo();
      appVersion = info.version;
    } catch {
      // keep 'unknown'
    }

    const browserName =
      platform !== 'web' && browser?.name
        ? `WebView (${browser.name})`
        : browser?.name ?? 'unknown';

    const orientation = window.screen?.orientation?.type ?? 'unknown';

    return {
      platform: platform as IFeedbackContext['platform'],
      appVersion,
      userAgent: navigator.userAgent,
      browser: {
        name: browserName,
        version: browser?.version ?? 'unknown',
        os: browser?.os ?? 'unknown'
      },
      screen: {
        windowWidth: window.innerWidth || window.screen?.width || 0,
        screenWidth: window.screen?.width || 0,
        screenHeight: window.screen?.height || 0,
        pixelDepth: window.screen?.pixelDepth || 0,
        orientation
      },
      locale,
      currentUrl: window.location.pathname,
      consoleErrors: [...this.consoleErrors],
      apiErrors: [...this.apiErrors],
      sentryEventId: null,
      timestamp: new Date().toISOString()
    };
  }

  private pushEntry<T>(buffer: T[], entry: T): void {
    if (buffer.length >= MAX_ENTRIES) {
      buffer.shift();
    }
    buffer.push(entry);
  }
}

export const feedbackContextCollector = new FeedbackContextCollector();
